import type { ReportRow } from '../../models/report.types'

import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { type CategorySlot, valuesByCategoryOccurrence } from './chartCategoryAxis'
import { chartFieldLabel } from './chartPresentation'

/**
 * Actual vs previous-period bars from the `prev_<metric>` comparison fields. One metric keeps the
 * overlapped, offset pair; several metrics each get a grouped pair whose previous bar is a faded
 * copy of the metric's own colour.
 */
export function compareSeries(
  input: BuildCategoricalChartOptionsInput,
  groups: ReadonlyMap<string, ReportRow[]>,
  axisFields: readonly string[],
  metrics: readonly string[],
  slots: readonly CategorySlot[],
  seriesType: 'line' | 'area' | 'column' | 'bar',
): NonNullable<CategoricalChartOptions['series']> {
  const rows = [...groups.values()].flat()
  const [single] = metrics
  if (metrics.length === 1 && single) {
    return [
      {
        type: seriesType,
        name: 'Actual',
        color: 'var(--ui-chart-compare-actual)',
        data: valuesByCategoryOccurrence(rows, axisFields, single, slots),
        zIndex: 2,
        grouping: false,
        pointPlacement: -0.12,
        pointWidth: 30,
        custom: { metric: single, compareRole: 'actual' },
      },
      {
        type: seriesType,
        name: 'Previous',
        color: 'var(--ui-chart-compare-previous)',
        data: valuesByCategoryOccurrence(rows, axisFields, `prev_${single}`, slots),
        zIndex: 1,
        grouping: false,
        pointPlacement: 0,
        pointWidth: 30,
        custom: { metric: single, compareRole: 'previous' },
      },
    ] as NonNullable<CategoricalChartOptions['series']>
  }
  return metrics.flatMap((metric, index) => [
    {
      type: seriesType,
      id: `compare-${metric}`,
      name: chartFieldLabel(input, metric),
      colorIndex: index,
      data: valuesByCategoryOccurrence(rows, axisFields, metric, slots),
      custom: { metric, compareRole: 'actual' },
    },
    {
      type: seriesType,
      linkedTo: `compare-${metric}`,
      name: `${chartFieldLabel(input, metric)} · Previous`,
      colorIndex: index,
      opacity: 0.45,
      data: valuesByCategoryOccurrence(rows, axisFields, `prev_${metric}`, slots),
      custom: { metric, compareRole: 'previous' },
    },
  ]) as NonNullable<CategoricalChartOptions['series']>
}
